/**
 * Login View
 *
 * Login screen using DopeUI with ASCII art logo.
 * Redirects to main menu on interaction.
 */

import { registerView } from '../viewRegistry';
import { interactiveRegistry } from '../interactivity';
import { getCanonicalSafeArea } from '../safeArea';
import { renderBlobBackground } from '../renderer/plasma';
import { generateAsciiArt, ensureFigletFont } from '../../utils/asciiArt';
import DopeUI, { TERMINAL, SPACING } from '../ui/DopeOS-UI';

const HERO_LINES = ['DOPE', 'CHAIN', 'WARS'];
const _asciiCache = {};
let _asciiReady = false;

async function ensureLoginAscii() {
  if (_asciiReady) return;
  try {
    await ensureFigletFont('ANSI Shadow', '/fonts/figlet/ANSI_Shadow.flf');
    for (const line of HERO_LINES) {
      _asciiCache[line] = await generateAsciiArt(line, { font: 'ANSI Shadow', width: 60 });
    }
    _asciiReady = true;
  } catch (err) {
    console.error('Failed to load login ASCII art:', err);
  }
}

// Start loading immediately
ensureLoginAscii();

registerView('login', function renderLogin(ctx, time, width, height, params) {
  // Clear interactions at start of frame
  interactiveRegistry.clear();

  const w = width || 640;
  const h = height || 400;
  const safe = getCanonicalSafeArea({ w, h });

  // Clear background
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, w, h);

  // Plasma background
  ctx.save();
  ctx.globalAlpha = 0.2;
  renderBlobBackground(ctx, time, w, h);
  ctx.restore();

  // Draw ASCII logo at top if ready - BIG & BOLD
  const isMobile = safe.width < 500; // Slightly higher threshold for mobile
  // MUCH BIGGER logo: 70% height on mobile (fills screen), 55% on desktop (max 220px)
  const maxLogoHeight = isMobile ? safe.height * 0.58 : Math.min(190, safe.height * 0.48);
  const logoHeight = _asciiReady ? maxLogoHeight : 0;

  if (_asciiReady) {
    const heroArt = _asciiCache[HERO_LINES[0]] + '\n' + _asciiCache[HERO_LINES[1]] + '\n' + _asciiCache[HERO_LINES[2]];
    const asciiLines = heroArt.split('\n');

    ctx.font = '100px monospace';
    const widestLine = asciiLines.reduce((a, b) => (a.length > b.length ? a : b));
    const widthAt100 = ctx.measureText(widestLine).width;

    // Use 98% of width for maximum impact on mobile
    const widthFactor = isMobile ? 0.98 : 0.95;
    const candidateByWidth = ((safe.width * widthFactor) / widthAt100) * 100;
    const candidateByHeight = logoHeight / asciiLines.length;
    const lineHeight = Math.max(1, Math.round(Math.min(candidateByWidth, candidateByHeight) * 0.98));
    ctx.font = `${lineHeight}px monospace`;

    const totalArtH = lineHeight * asciiLines.length;
    // Mobile: start at very top with minimal padding, Desktop: centered in upper portion
    const topOffset = isMobile ? 18 : Math.max(10, (safe.height * 0.15 - totalArtH / 2));
    let yCursor = safe.top + topOffset;

    // BOLD GLOW EFFECT - stronger on desktop
    ctx.fillStyle = TERMINAL.TEXT;
    ctx.shadowColor = TERMINAL.ACCENT || TERMINAL.TEXT;
    ctx.shadowBlur = isMobile ? 20 : 25;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';

    // Draw each line with centered alignment
    asciiLines.forEach((asciiLine, index) => {
      const lineW = ctx.measureText(asciiLine).width;
      const startX = safe.left + (safe.width - lineW) / 2;
      
      // Slight color variation for depth - middle line slightly brighter
      if (index === 1) { // CHAIN line
        ctx.shadowBlur = isMobile ? 25 : 35;
      } else {
        ctx.shadowBlur = isMobile ? 20 : 25;
      }
      
      ctx.fillText(asciiLine, startX, yCursor);
      yCursor += lineHeight;
    });

    // Reset shadow
    ctx.shadowBlur = 0;
  }

  // Main panel
  const panelWidth = Math.min(412, safe.width - 32);
  const panelHeight = 196;
  const panelX = safe.left + (safe.width - panelWidth) / 2;
  const desiredPanelY = safe.top + logoHeight + 20 + (safe.height - logoHeight - 20 - panelHeight) / 2;
  const panelY = Math.min(desiredPanelY, safe.height - panelHeight - 12);

  DopeUI.Panel(ctx, panelX, panelY, panelWidth, panelHeight, '');

  DopeUI.Header(ctx, 'ENTER THE UNDERWORLD', panelX + SPACING.MD, panelY + 22, 1.2);
  DopeUI.Text(ctx, 'GUEST SESSION // NO WALLET REQUIRED', panelX + SPACING.MD, panelY + 48, 'dim', 1);

  const isMobileLogin = safe.width < 400;
  const btnWidth = isMobileLogin ? Math.min(260, panelWidth - 36) : 232;
  const btnX = panelX + (panelWidth - btnWidth) / 2;
  const guestY = panelY + 68;
  const guestH = isMobileLogin ? 46 : 42;

  DopeUI.Button(ctx, btnX, guestY, btnWidth, guestH, 'ENTER AS GUEST', 'hover');
  interactiveRegistry.register({
    id: 'login-guest',
    x: btnX,
    y: guestY,
    w: btnWidth,
    h: guestH,
    focusable: true,
    onClick: () => {
      if (typeof params?.onGuestClick === 'function') params.onGuestClick();
    },
    onKey: (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (typeof params?.onGuestClick === 'function') params.onGuestClick();
      }
    }
  });

  const walletY = panelY + 124;
  const walletH = isMobileLogin ? 34 : 30;
  DopeUI.Button(ctx, btnX, walletY, btnWidth, walletH, 'CONNECT WALLET // OPTIONAL', 'normal');
  interactiveRegistry.register({
    id: 'login-connect',
    x: btnX,
    y: walletY,
    w: btnWidth,
    h: walletH,
    focusable: true,
    onClick: () => {
      if (typeof params?.onWaitlistClick === 'function') {
        params.onWaitlistClick();
      }
    },
    onKey: (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (typeof params?.onWaitlistClick === 'function') {
          params.onWaitlistClick();
        }
      }
    }
  });

  const authError = params?.authError ? String(params.authError).toUpperCase().slice(0, 42) : null;
  if (authError) {
    DopeUI.Text(ctx, `! ${authError}`, panelX + SPACING.MD, panelY + 160, 'dim', 0.8);
  } else {
    DopeUI.Text(ctx, 'WALLET LINK IS FOR CLAIM / TRANSFER LATER', panelX + SPACING.MD, panelY + panelHeight - 18, 'dim', 0.8);
  }
});

export default 'login';
